import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CalendarClock, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import {
  RadioCardGroup,
  type RadioCardOption,
} from '@/components/ui/radio-card-group';
import { type DeletionMode } from '@/hooks/useTenantDeletionMutations';
import { useCustomerDeleteHandlers } from '@/sections/customerManagement/hooks/actions';
import {
  ActionSource,
  CustomerData,
} from '@/sections/customerManagement/types';

/**
 * The modal only reads the tenant identifier (`email` == the A15 row key) and a
 * human label (`displayName`). Accepting this minimal shape lets non-table
 * callers (e.g. the settings surfaces) target a tenant without fabricating a
 * full `CustomerData`. `CustomerData[]` callers (CustomerTable) still fit, since
 * `Pick<>` only widens the accepted set.
 */
export type DeletionTargetCustomer = Pick<
  CustomerData,
  'email' | 'displayName'
>;

/** Grace-window bounds mirror the backend validation (A10: [1, 30] days). */
const GRACE_WINDOW_MIN = 1;
const GRACE_WINDOW_MAX = 30;
const GRACE_WINDOW_DEFAULT = 7;

const MODE_OPTIONS: RadioCardOption<DeletionMode>[] = [
  {
    value: 'scheduled',
    label: 'Schedule deletion',
    description:
      'Freezes the tenant now (scans stop, logins disabled). The permanent purge runs after the grace window and can be cancelled until then.',
    icon: CalendarClock,
  },
  {
    value: 'immediate',
    label: 'Delete immediately',
    description: 'Purges all tenant data now. This cannot be undone.',
    icon: Trash2,
  },
];

interface DeleteCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * A11: deletion is single-tenant. Only the FIRST entry is targeted — the
   * modal produces one name confirmation for one tenant. (Prop stays an array
   * for call-site compatibility; the bulk delete entry point is disabled in B9.)
   */
  customers: DeletionTargetCustomer[];
  source?: ActionSource;
  onSuccess?: () => void;
}

/**
 * Delete Customer Modal (single-tenant full-data purge).
 *
 * Requests a Praetorian-Admin-only tenant deletion. The operator picks a mode
 * (Scheduled by default, or Immediate) and must type the tenant identifier
 * (its email) to confirm; the typed value feeds `nameConfirmation`, which the
 * backend re-verifies against `tenant`.
 */
export const DeleteCustomersModal: React.FC<DeleteCustomersModalProps> = ({
  isOpen,
  onClose,
  customers,
  source: _source = 'drawer',
  onSuccess,
}) => {
  const { handleDeleteCustomer, isPending } = useCustomerDeleteHandlers();
  const [mode, setMode] = useState<DeletionMode>('scheduled');
  const [graceWindowDays, setGraceWindowDays] =
    useState<number>(GRACE_WINDOW_DEFAULT);
  const [confirmText, setConfirmText] = useState('');

  // Single tenant: `tenant` == the account email used as the row key (A15).
  const customer = customers[0];
  const trimmedConfirm = confirmText.trim();
  const isConfirmed = Boolean(customer) && trimmedConfirm === customer?.email;

  const handleGraceWindowChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsed = Number(event.target.value);
    if (Number.isNaN(parsed)) {
      setGraceWindowDays(GRACE_WINDOW_MIN);
      return;
    }
    setGraceWindowDays(
      Math.min(GRACE_WINDOW_MAX, Math.max(GRACE_WINDOW_MIN, Math.round(parsed)))
    );
  };

  const handleDelete = async () => {
    if (!customer || !isConfirmed) {
      toast.error(
        `Type ${customer?.email ?? 'the tenant identifier'} to confirm`
      );
      return;
    }

    try {
      await handleDeleteCustomer({
        tenant: customer.email,
        mode,
        graceWindowDays: mode === 'scheduled' ? graceWindowDays : undefined,
        nameConfirmation: trimmedConfirm,
      });

      toast.success(
        mode === 'immediate'
          ? `Account for ${customer.displayName} is being permanently deleted`
          : `Deletion scheduled for ${customer.displayName} in ${graceWindowDays} day${graceWindowDays === 1 ? '' : 's'}`
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      // Surface backend refusals verbatim (403 gate; 409 denylist / subtenant /
      // name-mismatch / lock) — the API wrapper puts the response text on `.name`.
      toast.error(
        error instanceof Error && error.name
          ? error.name
          : 'Failed to request tenant deletion'
      );
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setMode('scheduled');
    setGraceWindowDays(GRACE_WINDOW_DEFAULT);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Delete Customer"
      subtitle={`Confirm deletion of ${customer?.displayName ?? ''}`}
      size="lg"
      footer={{
        text: isPending ? 'Deleting...' : 'Delete',
        onClick: handleDelete,
        disabled: !isConfirmed || isPending,
      }}
    >
      <div className="flex flex-col gap-4">
        {/* Warning banner */}
        <div className="border-error-light bg-error-selected flex items-start gap-3 rounded-md border p-4">
          <ExclamationTriangleIcon className="text-error size-5 flex-shrink-0" />
          <div>
            <p className="text-error mb-1 text-sm font-medium">
              Warning: This action cannot be undone
            </p>
            <p className="text-error text-sm">
              {`Deleting ${customer?.displayName ?? 'this tenant'}'s account permanently removes all associated data across every store.`}
            </p>
          </div>
        </div>

        {/* Mode picker */}
        <div className="flex flex-col gap-2">
          <span className="text-chariot-text-primary text-sm font-medium">
            Deletion mode
          </span>
          <RadioCardGroup
            ariaLabel="Deletion mode"
            value={mode}
            onChange={setMode}
            columns={1}
            options={MODE_OPTIONS}
          />
        </div>

        {/* Grace window (scheduled only) */}
        {mode === 'scheduled' && (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="grace-window-days"
              className="text-chariot-text-primary text-sm font-medium"
            >
              Grace window (days)
            </label>
            <input
              id="grace-window-days"
              type="number"
              min={GRACE_WINDOW_MIN}
              max={GRACE_WINDOW_MAX}
              step={1}
              value={graceWindowDays}
              onChange={handleGraceWindowChange}
              disabled={isPending}
              className="border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary w-32 rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-hidden"
            />
            <p className="text-chariot-text-secondary text-xs">
              Between {GRACE_WINDOW_MIN} and {GRACE_WINDOW_MAX} days. The purge
              is cancellable until it runs.
            </p>
          </div>
        )}

        {/* Confirmation input */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="confirm-delete"
            className="text-chariot-text-primary text-sm font-medium"
          >
            Type{' '}
            <span className="text-error font-mono">
              {customer?.email ?? 'the tenant identifier'}
            </span>{' '}
            to confirm:
          </label>
          <input
            id="confirm-delete"
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder={customer?.email ?? ''}
            disabled={isPending}
            autoComplete="off"
            className="border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-hidden"
          />
        </div>
      </div>
    </Modal>
  );
};
